/**
 * BewerbungStep — öffentliche Stufe-2-/Stufe-3-Seite via Magic-Link.
 *
 * Route: /bewerbung/:tenant_slug/step/:token
 *
 * - GET /api/public/prospect/{token} → Prospect + tenant + listing + target_stage
 * - target_stage=stage2 → Detail-Formular (Adresse, Arbeitgeber, Einkommen, Schufa)
 * - target_stage=stage3 → Upload-Bereich für ID, Schufa, Gehaltsnachweise + freie
 *   Anhänge (max. 5)
 *
 * Whitelabel: nutzt tenant.primary_color + display_name.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UploadSimple, FileText, CheckCircle, ShieldCheck, Info, X } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const KIND_LABEL = {
  id_document: "Personalausweis / Reisepass",
  schufa: "Bonitätsauskunft (Schufa)",
  payslip: "Gehaltsnachweis (letzte 3 Monate)",
  other: "Sonstige Unterlage",
};

export default function BewerbungStep() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploaded, setUploaded] = useState([]);

  const load = useCallback(() => {
    setState({ loading: true, data: null, error: null });
    axios.get(`${API}/api/public/prospect/${token}`)
      .then(({ data }) => {
        setState({ loading: false, data, error: null });
        setForm({
          current_address: data.prospect.current_address || "",
          employer: data.prospect.employer || "",
          net_income_eur: data.prospect.net_income_eur || "",
          net_income_source_note: data.prospect.stage2_data?.net_income_source_note || "",
          move_out_notice_given: data.prospect.stage2_data?.move_out_notice_given || false,
          schufa_agreement: data.prospect.stage2_data?.schufa_agreement || false,
          additional_notes: data.prospect.stage2_data?.additional_notes || "",
        });
        setUploaded(data.prospect.stage3_documents || []);
      })
      .catch((e) => {
        const status = e?.response?.status;
        const msg = status === 410 ? "Ihr Link ist nicht mehr gültig — bitte fordern Sie einen neuen Link beim Vermieter an."
                  : status === 404 ? "Bewerbung nicht gefunden."
                  : "Der Link konnte nicht geladen werden.";
        setState({ loading: false, data: null, error: msg });
      });
  }, [token]);
  useEffect(load, [load]);

  const submitStage2 = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/prospect/${token}/stage2`, {
        current_address: form.current_address,
        coapplicants: [],
        employer: form.employer,
        net_income_eur: Number(form.net_income_eur) || null,
        net_income_source_note: form.net_income_source_note,
        move_out_notice_given: form.move_out_notice_given,
        schufa_agreement: form.schufa_agreement,
        additional_notes: form.additional_notes,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (kind, file, note) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    if (note) fd.append("note", note);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/public/prospect/${token}/stage3/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${KIND_LABEL[kind]} hochgeladen.`);
      setUploaded((u) => [...u, { doc_id: data.doc_id, kind, uploaded_at: new Date().toISOString(),
                                   filename: file.name, note }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const completeStage3 = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/prospect/${token}/stage3/complete`);
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Abschluss fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) return <PublicShell><div className="text-slate-500 text-center py-16">Lade …</div></PublicShell>;
  if (state.error) {
    return (
      <PublicShell>
        <div className="text-center py-16">
          <X size={40} className="text-rose-600 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900">Link nicht gültig</h2>
          <p className="text-sm text-slate-600 mt-2">{state.error}</p>
        </div>
      </PublicShell>
    );
  }
  const { data } = state;
  const stage = data.target_stage;
  const p = data.prospect;
  const tenant = data.tenant || {};
  const primary = tenant.primary_color || "#0F172A";

  if (submitted) {
    return (
      <PublicShell tenant={tenant}>
        <div className="text-center py-16">
          <CheckCircle size={54} weight="fill" className="mx-auto mb-4" style={{ color: primary }} />
          <h2 className="text-xl font-semibold text-slate-900">Vielen Dank!</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
            Ihre Angaben wurden übermittelt. Wir melden uns in Kürze bei Ihnen.
          </p>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell tenant={tenant} listing={data.listing}>
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">
            {stage === "stage2" ? "Schritt 2 von 3" : "Schritt 3 von 3"}
          </p>
          <h1 className="text-2xl font-bold text-slate-900">
            {stage === "stage2" ? "Ihre Detailangaben" : "Nachweise hochladen"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Guten Tag {p.first_name} {p.last_name}, bitte {stage === "stage2" ? "ergänzen Sie folgende Angaben" : "laden Sie Ihre Nachweise hoch"}.
          </p>
        </div>

        {stage === "stage2" && (
          <Stage2Form form={form} setForm={setForm} onSubmit={submitStage2} busy={busy} primary={primary} />
        )}
        {stage === "stage3" && (
          <Stage3Form uploaded={uploaded} onUpload={uploadFile} onComplete={completeStage3} busy={busy} primary={primary} />
        )}
      </div>
    </PublicShell>
  );
}

function Stage2Form({ form, setForm, onSubmit, busy, primary }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="mb-4"><Label className="text-xs text-slate-600 mb-1 block">Aktuelle Anschrift</Label>
        <Input value={form.current_address} onChange={(e) => setForm({ ...form, current_address: e.target.value })}
               placeholder="Straße, Hausnr., PLZ, Ort" data-testid="stage2-address" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="mb-4"><Label className="text-xs text-slate-600 mb-1 block">Arbeitgeber</Label>
          <Input value={form.employer} onChange={(e) => setForm({ ...form, employer: e.target.value })}
                 placeholder="z. B. Musterfirma GmbH" data-testid="stage2-employer" />
        </div>
        <div className="mb-4"><Label className="text-xs text-slate-600 mb-1 block">Nettoeinkommen (EUR/Monat)</Label>
          <Input type="number" step="0.01" value={form.net_income_eur}
                 onChange={(e) => setForm({ ...form, net_income_eur: e.target.value })}
                 placeholder="2500" data-testid="stage2-income" />
        </div>
      </div>
      <div className="mb-4"><Label className="text-xs text-slate-600 mb-1 block">Erläuterung Einkommen (optional)</Label>
        <Textarea rows={2} value={form.net_income_source_note}
                  onChange={(e) => setForm({ ...form, net_income_source_note: e.target.value })}
                  placeholder="z. B. Vollzeit, unbefristet seit 2023" />
      </div>
      <div className="flex items-start gap-2 mb-3">
        <Checkbox checked={form.move_out_notice_given}
                  onCheckedChange={(v) => setForm({ ...form, move_out_notice_given: !!v })}
                  data-testid="stage2-notice" />
        <span className="text-xs text-slate-700">Ich habe meine aktuelle Wohnung bereits gekündigt / kann kurzfristig einziehen.</span>
      </div>
      <div className="flex items-start gap-2 mb-3">
        <Checkbox checked={form.schufa_agreement}
                  onCheckedChange={(v) => setForm({ ...form, schufa_agreement: !!v })}
                  data-testid="stage2-schufa" />
        <span className="text-xs text-slate-700">
          Ich bin bereit, eine aktuelle Bonitätsauskunft (Schufa) vorzulegen.
        </span>
      </div>
      <div className="mb-4"><Label className="text-xs text-slate-600 mb-1 block">Zusätzliche Anmerkungen (optional)</Label>
        <Textarea rows={2} value={form.additional_notes}
                  onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
                  placeholder="z. B. Haustier, geplanter Einzugstermin, Fragen" />
      </div>
      <div className="text-xs text-slate-500 flex items-start gap-2 mb-4 bg-slate-50 border border-slate-200 rounded p-2">
        <ShieldCheck size={14} className="mt-0.5 flex-shrink-0" />
        Ihre Angaben werden ausschließlich zur Bearbeitung dieser Bewerbung verwendet. Die Verarbeitung
        erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO. Sie können Ihre Angaben jederzeit widerrufen.
      </div>
      <Button onClick={onSubmit} disabled={busy}
              style={{ backgroundColor: primary }} className="w-full text-white"
              data-testid="stage2-submit">
        {busy ? "Speichere …" : "Angaben absenden"}
      </Button>
    </div>
  );
}

function Stage3Form({ uploaded, onUpload, onComplete, busy, primary }) {
  const [otherNote, setOtherNote] = useState("");
  const KINDS = ["id_document", "schufa", "payslip"];
  const hasFor = (k) => uploaded.filter((u) => u.kind === k).length > 0;
  const otherCount = uploaded.filter((u) => u.kind === "other").length;

  return (
    <div className="space-y-4">
      {KINDS.map((k) => (
        <div key={k} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`stage3-slot-${k}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-slate-600" />
              <span className="font-medium text-slate-900">{KIND_LABEL[k]}</span>
              {hasFor(k) && <CheckCircle size={16} weight="fill" className="text-emerald-600" />}
            </div>
            {hasFor(k) && (
              <span className="text-xs text-emerald-700">
                {uploaded.filter((u) => u.kind === k).length} Datei(en) hochgeladen
              </span>
            )}
          </div>
          <label className="flex items-center gap-2 border-2 border-dashed border-slate-300 rounded p-3 cursor-pointer hover:bg-slate-50">
            <UploadSimple size={16} className="text-slate-500" />
            <span className="text-xs text-slate-600">PDF, JPG oder PNG hochladen …</span>
            <input type="file" accept=".pdf,image/*" className="hidden"
                   onChange={(e) => e.target.files?.[0] && onUpload(k, e.target.files[0])} />
          </label>
        </div>
      ))}

      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="stage3-slot-other">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-slate-600" />
            <span className="font-medium text-slate-900">Weitere Anhänge (optional, max. 5)</span>
          </div>
          {otherCount > 0 && <span className="text-xs text-slate-600">{otherCount} / 5</span>}
        </div>
        <Input value={otherNote} onChange={(e) => setOtherNote(e.target.value)}
               placeholder="Beschreibung (z. B. Mieterselbstauskunft, Vormieter-Bescheinigung)"
               className="mb-2 text-sm" />
        <label className={`flex items-center gap-2 border-2 border-dashed border-slate-300 rounded p-3 ${otherCount >= 5 ? "opacity-40" : "cursor-pointer hover:bg-slate-50"}`}>
          <UploadSimple size={16} className="text-slate-500" />
          <span className="text-xs text-slate-600">Datei hinzufügen …</span>
          <input type="file" disabled={otherCount >= 5} accept=".pdf,image/*" className="hidden"
                 onChange={(e) => e.target.files?.[0] && (onUpload("other", e.target.files[0], otherNote), setOtherNote(""))} />
        </label>
      </div>

      {uploaded.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="text-xs font-semibold text-slate-600 uppercase mb-2">Hochgeladene Nachweise</div>
          <ul className="space-y-1">
            {uploaded.map((u, i) => (
              <li key={i} className="flex items-center justify-between text-xs text-slate-700">
                <span><FileText size={12} className="inline mr-1" /> {KIND_LABEL[u.kind]}: {u.filename}</span>
                <CheckCircle size={12} weight="fill" className="text-emerald-600" />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-xs text-slate-500 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded p-3">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        Nach dem Klick auf &bdquo;Bewerbung abschließen&ldquo; werden Ihre Unterlagen an den Vermieter übermittelt.
        Sie erhalten eine Bestätigung sobald die Prüfung abgeschlossen ist.
      </div>

      <Button onClick={onComplete} disabled={busy || uploaded.length === 0}
              style={{ backgroundColor: primary }} className="w-full text-white"
              data-testid="stage3-complete">
        {busy ? "Sende …" : "Bewerbung abschließen"}
      </Button>
    </div>
  );
}

function PublicShell({ children, tenant, listing }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">{tenant?.display_name || tenant?.name || "Bewerbung"}</div>
          {listing && (
            <div className="text-xs text-slate-500">Bewerbung: {listing.title}</div>
          )}
        </div>
      </header>
      {children}
    </div>
  );
}
